import type {QueryClient} from "@tanstack/react-query";
import type {ZodType} from "zod";

import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {getErrorFromUnknown} from "@vesta/global-common-typescript/utilities/typeValidationUtilities";
import {GEOGRAPHY_MASTER_REVISIONS} from "@vesta/vos-types/modules/platformLevel/geography/domain/GeographyMasterRevisions";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {getGeographyMasterCache, type GeographyMasterCache} from "~/domainModels/indexedDb/geographyDatabase";
import {loadGeographyMaster} from "~/utilities/geographyMasterCache";
import {countryValidator, type Country} from "@vesta/vos-types/modules/platformLevel/geography/domain/Country";
import {indiaCityValidator, type IndiaCity} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaCity";
import {indiaPincodeValidator, type IndiaPincode} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaPincode";
import {indiaStateValidator, type IndiaState} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaState";
import {indiaZoneValidator, type IndiaZone} from "@vesta/vos-types/modules/platformLevel/geography/domain/IndiaZone";
import type {GeographyCollections} from "~/contexts/geographyContext";
import {initializeGeographyValueResolver} from "~/utilities/geographyValueResolver";
import {COUNTRIES__QUERY_KEY, INDIA_CITIES__QUERY_KEY, INDIA_PINCODES__QUERY_KEY, INDIA_STATES__QUERY_KEY, INDIA_ZONES__QUERY_KEY} from "~/utilities/queryKeys";

export async function getGeographyMasterWithCaching<TItem>({
    intellsysBaseUrl,
    path,
    queryClient,
    queryKey,
    revisionId,
    validator,
    cache,
}: {
    intellsysBaseUrl: string;
    path: string;
    queryClient: QueryClient;
    queryKey: string;
    revisionId: Uuid;
    validator: ZodType<TItem>;
    cache?: GeographyMasterCache;
}): Promise<Result<Array<TItem>>> {
    try {
        const data = await queryClient.ensureQueryData({
            queryKey: [queryKey, new URL(intellsysBaseUrl).origin, revisionId],
            queryFn: () => loadGeographyMaster({intellsysBaseUrl, path, revisionId, validator, cache: cache ?? getGeographyMasterCache()}),
            retry: false,
            staleTime: Infinity,
        });
        return dataResult(data);
    } catch (error) {
        return errorResult(getErrorFromUnknown(error));
    }
}

export function getCountriesWithCaching(args: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    cache?: GeographyMasterCache;
}): Promise<Result<Array<Country>>> {
    return getGeographyMasterWithCaching({
        ...args,
        path: "/geography/countries/get",
        queryKey: COUNTRIES__QUERY_KEY,
        revisionId: GEOGRAPHY_MASTER_REVISIONS.countries,
        validator: countryValidator,
    });
}

function unwrap<TItem>(result: Result<Array<TItem>>): Array<TItem> {
    if (result.success !== true) {
        throw result.error;
    }
    return result.data;
}

export async function getGeographyWithCaching({
    intellsysBaseUrl,
    queryClient,
    cache,
}: {
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    cache?: GeographyMasterCache;
}): Promise<GeographyCollections> {
    const [countriesResult, indiaCitiesResult, indiaPincodesResult, indiaStatesResult, indiaZonesResult] = await Promise.all([
        getGeographyMasterWithCaching<Country>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/countries/get",
            queryClient: queryClient,
            queryKey: COUNTRIES__QUERY_KEY,
            revisionId: GEOGRAPHY_MASTER_REVISIONS.countries,
            cache,
            validator: countryValidator,
        }),
        getGeographyMasterWithCaching<IndiaCity>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-cities/get",
            queryClient: queryClient,
            queryKey: INDIA_CITIES__QUERY_KEY,
            revisionId: GEOGRAPHY_MASTER_REVISIONS.indiaCities,
            cache,
            validator: indiaCityValidator,
        }),
        getGeographyMasterWithCaching<IndiaPincode>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-pincodes/get",
            queryClient: queryClient,
            queryKey: INDIA_PINCODES__QUERY_KEY,
            revisionId: GEOGRAPHY_MASTER_REVISIONS.indiaPincodes,
            cache,
            validator: indiaPincodeValidator,
        }),
        getGeographyMasterWithCaching<IndiaState>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-states/get",
            queryClient: queryClient,
            queryKey: INDIA_STATES__QUERY_KEY,
            revisionId: GEOGRAPHY_MASTER_REVISIONS.indiaStates,
            cache,
            validator: indiaStateValidator,
        }),
        getGeographyMasterWithCaching<IndiaZone>({
            intellsysBaseUrl: intellsysBaseUrl,
            path: "/geography/india-zones/get",
            queryClient: queryClient,
            queryKey: INDIA_ZONES__QUERY_KEY,
            revisionId: GEOGRAPHY_MASTER_REVISIONS.indiaZones,
            cache,
            validator: indiaZoneValidator,
        }),
    ]);

    const geography = {
        countries: unwrap(countriesResult),
        indiaCities: unwrap(indiaCitiesResult),
        indiaPincodes: unwrap(indiaPincodesResult),
        indiaStates: unwrap(indiaStatesResult),
        indiaZones: unwrap(indiaZonesResult),
    };
    initializeGeographyValueResolver(geography);
    return geography;
}
